import { useCallback, useMemo } from 'react';
import { useCachedList } from '../../../../hooks/useCachedList';
import { hormoneDataCache, serializeHormoneDataKey } from '../../../../utils/listCache/caches';
import { addDays, localToday } from '../model/dates';
import {
  fetchCravingLogs,
  fetchCravingSummary,
  fetchEnergyLogs,
  fetchEnergySummary,
  fetchPeriodLogs,
  fetchPeriodSummary,
  fetchSymptomLogs,
  fetchSymptomSummary,
} from './hormoneApi';

// One year back: the longest compare range ("1 year"), and the raw-fetch cap
// (the server refuses a range of 366 days or more).
export const HISTORY_DAYS = 365;

/**
 * Loads everything the Hormonal Wellness tab shows, in 8 parallel GETs:
 * the 4 summaries over the last 12 months (each compare range filters their
 * cycles client-side) and the 4 raw logs over the same year (calendar, day
 * detail, per-day pain, weekly energy).
 *
 * @param {string} userId - The client's UserProfile id.
 * @returns {{ data: object|undefined, today: string, loading: boolean, error: any, refresh: () => Promise<void> }}
 */
export function useHormoneData(userId) {
  const today = useMemo(() => localToday(), []);
  const key = userId ? serializeHormoneDataKey(userId, today) : null;

  const fetcher = useCallback(async () => {
    const start = addDays(today, -HISTORY_DAYS);
    const [period, symptoms, energy, cravings, periodLogs, symptomLogs, energyLogs, cravingLogs] =
      await Promise.all([
        fetchPeriodSummary(userId, start, today),
        fetchSymptomSummary(userId, start, today),
        fetchEnergySummary(userId, start, today),
        fetchCravingSummary(userId, start, today),
        fetchPeriodLogs(userId, start, today),
        fetchSymptomLogs(userId, start, today),
        fetchEnergyLogs(userId, start, today),
        fetchCravingLogs(userId, start, today),
      ]);
    return {
      today,
      historyStart: start,
      summaries: { period, symptoms, energy, cravings },
      logs: { period: periodLogs, symptoms: symptomLogs, energy: energyLogs, cravings: cravingLogs },
    };
  }, [userId, today]);

  const { data, loading, error, refresh } = useCachedList({
    cache: hormoneDataCache,
    key,
    fetcher,
    enabled: Boolean(userId),
  });

  return { data, today, loading, error, refresh };
}
